'use strict';
/* ==========================================================================
   levels/video.js — step 1, BLUE FROM WHERE?: the interactive video (shared/engine/v2/core/player.js), collage with
   an antique botanical-plate look (DESIGN.md §2), one scene per script segment (content/script.json s1–s5; each
   segment's "visual" is the storyboard). Patterns: lessons/branches/levels/video.js (stamps, cards, captions above
   the stage, chapter cards) and lessons/through-zero/levels/video.js (the numbered working on EVERY branch).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.E / ctx.W
   (via cues(), which turns the ±Infinity of other parts into ±1e6), so the choreography follows whatever the voice
   timing is (text timing now, the recorded clips later, with no code change) and the player can seek, replay and run
   at 25×. Branches: a reply cue is min() over the question's reply ids (branch()): in the reply part only the branch
   that plays is finite, in the lines part all are +∞ (not yet), in the after part all are −∞ (done). A visual that
   belongs to ONE branch is gated on the answer (ansOf()).

   Honesty (DESIGN.md §4): every genotype, Punnett box, phenotype and percent on screen comes from core-local/cross.js
   (Cross.square / phenotype / phenotypes / percent / TRAITS / MENDEL / SINGLE_PLANTS); the working is the script's
   `working` evaluated by the shared bank evaluator (Bank.fill / Bank.eval / Bank.format) and cross-checked against
   Cross (nums().checks). The only literals are the crosses the dialogue SAYS (S1_CROSS: s1_07 "Mom and Dad could each
   be big B, little b"; S2_CROSS: s2_02 "Both of these plants are big Y, little y"), the family's eye colors the story
   gives (s1_01: Mom brown, Dad brown, Lily blue — cross-checked against the model), the storyboard's year 1865 (facts
   §Mendel's F2 counts: read in 1865), and the storyboard's own words for the "WHAT I LEARNED" card and the clipping.
   Eye color is ALWAYS labelled "SIMPLIFIED MODEL" (facts 2.6, 5.6).

   Staging: each segment opens on a chapter card (a botanical plate: seal + seed-packet label) that tears away; Lily
   (left) and Mr. Mendoza (right) stand full size, captions in the top band on the speaker's side; the boards use the
   middle (x 440–1480, y 230–930). Portrait: the player puts the captions in the dock; the family card, the model key,
   the working, the ledgers and the WHAT I LEARNED card are also written into the dock (Dock.text).
   Cost: backdrops, mini trays and pea rows are baked once into layers (OFF budget); paper pieces are cached sprites;
   no per-frame shadowOn(). Art (each optional, paper stand-ins until it lands): IMG.lily / lily_point / mendoza /
   mendoza_point (core-local/hooks.js poses), greenhouse, pea_plant (core-local/plate.js), pea_pod, seed_packet.
   SFX (optional, shared fallbacks): pod_pop, watering, page. QA: tools/qa_video.js; debug: window.BlueEyesVideo.
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'", SLAB = "'Alfa Slab One'";
  const INK = '#2b2420', PENCIL = 'rgba(43,36,32,0.62)', RED = '#b3261e', NAVY = '#1f3b2a';
  const PC = () => Plate.C;
  // the crosses the dialogue says (see the header); everything else is computed from them
  const S1_CROSS = ['Bb', 'Bb'];      // s1_07 / q1: Mom and Dad are each big B, little b (the SIMPLIFIED eye model)
  const S2_CROSS = ['Yy', 'Yy'];      // s2_02: both plants are big Y, little y (Mendel's seed color)
  const MENDEL_YEAR = 1865;           // storyboard s3 ledger (facts §Mendel's F2 counts: read 8 Feb and 8 Mar 1865)
  const LEARNED = [                   // the storyboard's s5 card, word for word (content/script.json s5 visual)
    '1. Genes come in pairs: one from each parent.',
    '2. Alleles: dominant shows with one copy; recessive needs two.',
    '3. Genotype = the letters. Phenotype = what you see.',
    '4. A Punnett square gives percents, and every offspring is a new draw.',
  ];
  const CLIPPING = { head: 'TONGUE ROLLING:', body: 'not a one-gene trait after all (the scientist who proposed it said so)' };   // storyboard s5, facts 5.7

  /* ---------------- small helpers ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  const fmtInt = n => Math.round(n).toLocaleString('en-US');
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const segById = id => segs().find(s => s.id === id);
  const fx = (name, alt) => (Audio.has(name) ? name : alt);
  const ansOf = (ctx, part) => { const q = part.seg.question; if (!q) return null; return ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct; };
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const WRAPS = new Map();
  function wrapLines(str, maxW, size, font = HANDF) {
    const key = str + '|' + maxW + '|' + size + '|' + font;
    if (WRAPS.has(key)) return WRAPS.get(key);
    const words = String(str).split(/\s+/), out = []; let cur = '';
    for (const w of words) { const nx = cur ? cur + ' ' + w : w; if (cur && tw(nx, `${size}px ${font}`) > maxW) { out.push(cur); cur = w; } else cur = nx; }
    if (cur) out.push(cur);
    if (fontOk()) { if (WRAPS.size > 500) WRAPS.clear(); WRAPS.set(key, out); }
    return out;
  }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* static text (no write-on) */
  function txt(str, x, y, size, o = {}) {
    g.save(); g.font = `${size}px ${o.font || HANDF}`; g.fillStyle = o.color || INK; g.textAlign = o.align || 'center'; g.textBaseline = 'middle';
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    if (o.rot) { g.translate(x, y); g.rotate(o.rot); g.fillText(str, 0, 0); } else g.fillText(str, x, y);
    g.restore();
  }
  /* the min over a question's reply ids of a cue word per branch (tbl: {replyId: word | [word, n]}) */
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v == null) continue; const [w, n] = Array.isArray(v) ? v : [v, 1]; best = Math.min(best, c.W(id, w, n)); }
    return best;
  }
  const replyIds = q => q.right.concat(...Object.values(q.wrong || {}));
  const replyStart = (c, q) => Math.min(...replyIds(q).map(id => c.L(id)));
  const docked = () => typeof UI !== 'undefined' && UI.docked && UI.docked();

  /* ======================================================================
     Layers: static art baked once into an off-screen canvas (the engine's OFF budget evicts them like sprites).
     layer(key, x, y, w, h, draw, {zoom}) — draw() paints the rect (x, y, w, h) in the caller's coordinates.
     ====================================================================== */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }
  const artKey = (...names) => names.map(n => (IMG[n] ? 1 : 0)).join('');

  /* ---------------- backdrops (baked) ---------------- */
  /* the greenhouse: aged paper, the plate border, the glasshouse (art or panes), a potting bench, vines at the edges;
     a paper wash keeps it behind the boards */
  function bgGreen() {
    layer('bg_green|' + artKey('greenhouse', 'pea_plant'), 0, 0, 1920, 1080, () => {
      const C = PC();
      Plate.backdrop(-1e6);
      Plate.greenhouse(960, 500, 1500, 860, { seed: 7 });
      g.fillStyle = 'rgba(239,228,200,0.38)'; g.fillRect(0, 0, 1920, 1080);
      paperRect(960, 1020, 2000, 150, C.wood, { seed: 21, depth: 1.2, boil: false });
      ink([[0, 962], [1920, 966]], { w: 3, color: 'rgba(60,35,15,0.5)', seed: 22, passes: 1, step: 80 });
      Plate.vine(70, 990, 760, { seed: 11, side: 1, sway: 0 });
      Plate.vine(1850, 990, 740, { seed: 13, side: -1, sway: 0 });
    }, { zoom: 0.75 });
  }
  /* the potting-shed pinboard: kraft, the plate border, a shelf with seed packets */
  function bgShed() {
    layer('bg_shed|' + artKey('pea_plant'), 0, 0, 1920, 1080, () => {
      const C = PC();
      g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
      g.fillStyle = 'rgba(214,176,128,0.55)'; g.fillRect(0, 0, 1920, 1080);
      g.save(); g.strokeStyle = 'rgba(70,45,20,0.45)'; g.lineWidth = 3; g.strokeRect(34, 34, 1852, 1012); g.lineWidth = 1.2; g.strokeRect(46, 46, 1828, 988); g.restore();
      const r = rng(41);
      for (let i = 0; i < 60; i++) { g.fillStyle = `rgba(70,40,15,${0.04 + r() * 0.07})`; g.beginPath(); g.arc(r() * 1920, r() * 1080, 1 + r() * 3, 0, TAU); g.fill(); }
      // two seed packets pinned in the top corners (the shed's catalogue wall)
      Plate.packet(210, 200, 150, 200, { title: 'PEAS', color: C.leaf, seed: 362, rot: -0.06, pic: { trait: 'seedColour', value: 'yellow' } });
      Plate.packet(1710, 210, 150, 200, { title: 'PEAS', color: C.violet, seed: 363, rot: 0.05, pic: { trait: 'flowerColour', value: 'violet' } });
      paperRect(960, 1020, 2000, 150, C.woodDark, { seed: 23, depth: 1.2, boil: false });
      ink([[0, 962], [1920, 966]], { w: 3, color: 'rgba(40,25,10,0.5)', seed: 24, passes: 1, step: 80 });
      Plate.vine(70, 990, 700, { seed: 15, side: 1, sway: 0 });
      Plate.vine(1850, 990, 680, { seed: 17, side: -1, sway: 0 });
    }, { zoom: 0.75 });
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, color = RED, w = 9) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  function tick(x, y, s, prog, seed = 1, color = PC().leafDark) { ink([[x - 30 * s, y - 2 * s], [x - 8 * s, y + 22 * s], [x + 34 * s, y - 32 * s]], { w: 9 * s, color, seed, prog, passes: 2, step: 8 }); }
  /* a paper label: str on a paper rect (x, y = centre) */
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0.001) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    const font = `${size}px ${o.font || HANDF}`, w = Math.round(tw(str, font) + size * 0.9), h = Math.round(size * 1.4);
    paperRect(0, 0, w, h, o.color || PC().label, { seed: o.seed || 41, depth: o.depth ?? 1.0, outline: true });
    g.font = font; g.fillStyle = o.ink || INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  /* a sticky note with hand-written lines (lineT: one write-on time per line); strike = a red line across it */
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 300, size = o.size || 36, seed = o.seed || 610, h = Math.round(44 + lines.length * size * 1.15);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || '#f2d479', { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, -h / 2 + 22 + size * 0.62 + i * size * 1.15, size, o.lineT ? o.lineT[i] : t0 + 0.1 + i * 0.12, { color: o.ink || INK, seed: seed + i + 1, dur: 0.35 }));
    if (o.strike != null) strike(-w / 2 + 16, h / 2 - 18, w / 2 - 16, -h / 2 + 18, P(T, o.strike, o.strike + 0.35), seed + 9);
    tape(0, -h / 2 + 2, 96, 0.05, undefined, seed + 20);
    g.restore();
  }
  /* a rubber stamp (no knock-out specks: they would punch through the whole frame) */
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px ${TYPE}`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));
  /* a cream card with tape (x, y = centre) */
  function card(x, y, w, h, s, o = {}) {
    if (s <= 0.001) return false;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.008); g.scale(s, s);
    paperRect(0, 0, Math.round(w), Math.round(h), o.color || '#fbf5e3', { seed: o.seed || 500, depth: 1.3, boil: false });
    if (o.rule !== false) { g.strokeStyle = 'rgba(80,60,35,0.3)'; g.lineWidth = 2; g.strokeRect(-w / 2 + 12, -h / 2 + 12, w - 24, h - 24); }
    if (o.tape !== false) tape(0, -h / 2 + 2, 120, 0.03, undefined, (o.seed || 500) + 1);
    g.restore();
    return true;
  }
  /* a terracotta pot with a pea vine (x, y = the pot's base) */
  const POT = Object.assign([[-50, -72], [50, -72], [37, 0], [-37, 0]], { k: 'blue:pot' });
  function pot(x, y, vineH, o = {}) {
    const s = o.s ?? 1; if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.scale(s, s); g.translate(-x, -y);
    if (vineH > 0) Plate.vine(x, y - 70, vineH, { seed: o.seed || 51, side: o.side || 1, sway: 0.6 });
    paperPiece(POT, x, y, '#b5643c', { seed: (o.seed || 51) + 1, depth: 1.2 });
    paperRect(x, y - 72, 116, 22, '#c9794c', { seed: (o.seed || 51) + 2, depth: 0.6, boil: false });
    paperRect(x, y - 86, 92, 10, PC().soil, { seed: (o.seed || 51) + 3, depth: 0, boil: false });
    g.restore();
  }
  /* a pea pod prop: the art, else the plate's paper pod */
  function podProp(x, y, h, rot, s, seed = 71) {
    if (s <= 0.001) return;
    if (IMG.pea_pod) { cut('pea_pod', x, y, h, { rot, s, seed, depth: 0.9 }); return; }
    // the plate's paper pod, with the peas showing through as bumps (until IMG.pea_pod lands)
    const r = h * 0.3;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    Plate.pheno('pod', 'inflated', 0, 0, r, { seed });
    for (let k = -1; k <= 1; k++) inkCircle(k * r * 0.62, 0, r * 0.3, { w: 2, color: 'rgba(43,36,32,0.45)', seed: seed + 10 + k, passes: 1 });
    g.restore();
  }
  /* one pea (a phenotype picture), rotated (it rolls) */
  function pea(value, x, y, r, rot = 0, s = 1, seed = 5) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); Plate.pheno('pea', value, 0, 0, r, { seed, s }); g.restore();
  }

  /* ======================================================================
     Staging: puppets, captions, chapter cards (after lessons/branches/levels/video.js)
     ====================================================================== */
  const POS = { lily: { x: 250, y: 1088, h: 560 }, mendoza: { x: 1665, y: 1088, h: 610 } };
  const LILY_POINT = new Set(['s1_01', 's2_04', 's3_01', 's4_01', 's4_05', 's5_06']);
  const MEN_POINT = new Set(['s1_05', 's1_07', 's2_02', 's2_03', 's2_06', 's3_02', 's3_04', 's4_04', 's4_06', 's5_05']);
  function cast(t, ctx) {
    const cap = ctx.caption, id = cap ? cap.id : '', who = cap ? cap.who : '';
    const lp = who === 'lily' && LILY_POINT.has(id) ? 'point' : null;
    const mp = who === 'mendoza' && (MEN_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('lily', POS.lily.x, POS.lily.y, POS.lily.h, { env: ctx.speaking('lily'), facing: 1, t, pose: lp });
    Puppet.draw('mendoza', POS.mendoza.x, POS.mendoza.y, POS.mendoza.h, { env: ctx.speaking('mendoza'), facing: -1, t, pose: mp });
    bubble(ctx);
  }
  /* caption bubbles: ≤ 2 rows (wider first, then paged at sentence ends, timed by the words), in the top band */
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  function bubble(ctx) {
    const l = ctx.caption; if (!l) return;
    const p = POS[l.who]; if (!p) return;
    const lily = l.who === 'lily', k = BS() / 40;
    const f = fit(l, Math.round(720 * k), Math.round(880 * k));
    const w = f.w, bh = f.h;
    let cx = lily ? p.x - 70 + w / 2 : p.x + 70 - w / 2;
    const cy = Math.max(bh / 2 + 16, 80 + bh / 2);
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    const cap = { x: cx, y: cy, side: lily ? 'left' : 'right', w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  /* ---------- chapter cards: a botanical plate (seal + seed-packet label) holds, then tears away to the scene ---------- */
  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  const TITLES = new Map();
  function titleLines(title) {
    if (TITLES.has(title)) return TITLES.get(title);
    const full = Plate.woodWidth(title, 100);
    let lines = [title];
    if (full * 0.9 > 1450) {
      const words = title.split(' '); let best = null;
      for (let i = 1; i < words.length; i++) { const a = words.slice(0, i).join(' '), b = words.slice(i).join(' '), m = Math.max(Plate.woodWidth(a, 100), Plate.woodWidth(b, 100)); if (!best || m < best.m) best = { m, L: [a, b] }; }
      if (best) lines = best.L;
    }
    if (fontOk()) TITLES.set(title, lines);
    return lines;
  }
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, C = PC();
    layer('card_bg|' + artKey('pea_plant'), 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
      paperRect(960, 548, 1640, 900, C.paper, { seed: 900, depth: 1.6, boil: false });
      g.save(); g.strokeStyle = 'rgba(80,60,35,0.45)'; g.lineWidth = 3; g.strokeRect(170, 128, 1580, 840); g.lineWidth = 1.2; g.strokeRect(184, 142, 1552, 812); g.restore();
      Plate.vine(290, 930, 640, { seed: 11, side: 1, sway: 0 });
      Plate.vine(1630, 930, 640, { seed: 13, side: -1, sway: 0 });
    }, { zoom: 0.5 });
    txt('CHAPTER', 960, 262, 46, { font: TYPE, color: C.leafDark });
    Plate.seal(960, 364, 64, { text: String(n), seed: 910 + n });
    const lines = titleLines(String(seg.chapter || seg.id).toUpperCase());
    const size = Math.round(Math.min(92, ...lines.map(l => 1400 / (Plate.woodWidth(l, 100) / 100 + 1.4))));
    const y0 = lines.length > 1 ? 540 : 590;
    lines.forEach((l, k) => Plate.label(l, 960, y0 + k * size * 1.95, size, { seed: 950 + n * 7 + k, rot: k ? 0.008 : -0.01 }));
    hand('~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~', 960, 860, 42, -1e6, { dur: 0.01, color: 'rgba(47,82,38,0.75)', seed: 960 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  /* ======================================================================
     the numbers: the dialogue's crosses through the model (cross.js)
     ====================================================================== */
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const X = window.Cross, eye = X.TRAITS.eyeModel, pe = X.TRAITS.seedColour;
    const sq1 = X.square(S1_CROSS[0], S1_CROSS[1], 'eyeModel');
    const kid = sq1.boxes.find(b => !b.dominant);                          // the one bb box: blue, in the model
    const parent = X.phenotype('eyeModel', S1_CROSS[0]);                   // Bb → brown
    const sq2 = X.square(S2_CROSS[0], S2_CROSS[1]);
    const recIdx = sq2.boxes.map((b, i) => (b.dominant ? -1 : i)).filter(i => i >= 0);
    const hetIdx = sq2.boxes.map((b, i) => (b.dominant && b.genotype[0] !== b.genotype[1] ? i : -1)).filter(i => i >= 0);
    const recG = sq2.boxes[recIdx[0]].genotype;
    const ph = X.phenotypes(S2_CROSS[0], S2_CROSS[1]);                     // {yellow: 75, green: 25}
    const pGreen = X.percent(S2_CROSS[0], S2_CROSS[1], { kind: 'geno', value: recG });
    const m = X.MENDEL.find(r => r.trait === 'seedColour'), tot = X.MENDEL.total;
    // Mendel's single plant with the most green (the storyboard's "one plant wobbles": 20 yellow, 19 green)
    const plant = X.SINGLE_PLANTS.filter(r => r.trait === 'seedColour').reduce((a, b) => (b.r / (b.d + b.r) > a.r / (a.d + a.r) ? b : a));
    const BAR = 12, barY = Math.round(BAR * m.d / (m.d + m.r));
    const checks = [];
    const q2 = segById('s2').question, q4 = segById('s4').question;
    for (const q of [q2, q4]) {
      const gv = q.working.given;
      if (gv.boxes !== sq2.boxes.length) checks.push(`${q.id}: boxes ${gv.boxes} ≠ model ${sq2.boxes.length}`);
      if (gv.green !== recIdx.length) checks.push(`${q.id}: green ${gv.green} ≠ model ${recIdx.length}`);
      const v = Bank.eval(q.working.steps[q.working.steps.length - 1].formula, gv);
      if (Math.abs(v - pGreen) > 1e-9) checks.push(`${q.id}: working ${v} ≠ model ${pGreen}`);
      if (String(q.options[q.correct]).indexOf(pGreen + '%') < 0) checks.push(`${q.id}: correct option ≠ ${pGreen}%`);
    }
    if (kid.phenotype !== eye.recessive || parent !== eye.dominant) checks.push('s1: the model does not give brown parents a blue child');
    if (checks.length) console.warn('[video] model checks', checks);
    NUMS = {
      eye, pe, sq1, kid, parent, sq2, recIdx, hetIdx, recG, ph, pGreen, m, tot, plant, BAR, barY, checks,
      family: [{ who: 'MOM', g: sq1.p1, eye: parent }, { who: 'DAD', g: sq1.p2, eye: X.phenotype('eyeModel', S1_CROSS[1]) }, { who: 'LILY', g: kid.genotype, eye: kid.phenotype }],
    };
    return NUMS;
  }

  /* ======================================================================
     THE WORKING, STEP BY STEP (DESIGN.md §5.1, like lessons/through-zero): each question's `working` ({given, steps})
     written on a seed-catalogue card as numbered steps in time with Mr. Mendoza's reply, on the right AND every wrong
     branch, then the answer line (the correct option). Texts: Bank.fill; values: Bank.eval + Bank.format.
     ====================================================================== */
  const WK = new Map();
  function working(q) {
    if (WK.has(q.id)) return WK.get(q.id);
    const W_ = q.working; if (!W_) { WK.set(q.id, null); return null; }
    const steps = W_.steps.map((st, i) => {
      const filled = Bank.fill(st.text, W_.given).trim(), v = Bank.eval(st.formula, W_.given), value = Bank.format(v, st.format || 'text');
      let label = '', maths;
      if (/:$/.test(filled)) { label = filled.slice(0, -1).trim(); maths = value; }
      else maths = filled + ' ' + value;
      return { i, n: i + 1, label, maths, value, v, text: filled + ' ' + value };
    });
    const opt = String(q.options[q.correct]), wk = { steps, answer: opt.includes(':') ? opt : 'Answer: ' + opt };
    WK.set(q.id, wk);
    return wk;
  }
  /* per branch, the reply word each step lands on (null = the start of the reply) */
  const STEPCUE = {
    q2: { q2_right: [null, 'box', 'four'], q2_wrong0: ['Green', 'one', 'Twenty-five'], q2_wrong1: ['One', 'four', 'Twenty-five'] },
    q4: { q4_right: ['Every', 'draw', 'square'], q4_wrong0: ['fresh', 'one', 'Twenty-five'], q4_wrong2: ['new', 'one', 'Twenty-five'] },
  };
  function stepTimes(c, q) {
    const wk = working(q); if (!wk) return null;
    const tbl = STEPCUE[q.id] || {}, out = [], cardIn = replyStart(c, q) + 0.3;
    let prev = cardIn;
    wk.steps.forEach((st, i) => {
      let cue = Infinity;
      for (const id in tbl) { const w = tbl[id][i]; if (w == null) { cue = Math.min(cue, c.L(id)); continue; } const [ww, nn] = Array.isArray(w) ? w : [w, 1]; cue = Math.min(cue, c.W(id, ww, nn)); }
      const t0 = Math.max(cue, prev + (i ? 0.75 : 0.25));
      out.push(t0); prev = t0;
    });
    const hi = prev + 0.7;
    return { steps: out, hi, answer: hi + 0.35, cardIn };
  }
  const WL = { top: 74, label: 28, lrow: 34, maths: 54, mrow: 70, ans: 44 };
  const WLAY = new Map();
  function wkLayout(q, W) {
    const key = q.id + '|' + W; if (WLAY.has(key)) return WLAY.get(key);
    const wk = working(q);
    const rows = wk.steps.map(st => { const lines = st.label ? wrapLines(st.label, W - 110, WL.label, TYPE) : []; return { lines, h: lines.length * WL.lrow + WL.mrow }; });
    const ans = wrapLines(wk.answer, W - 110, WL.ans);
    const H = WL.top + rows.reduce((a, r) => a + r.h, 0) + 12 + ans.length * 50 + 26;
    const L = { rows, ans, H };
    if (fontOk()) WLAY.set(key, L);
    return L;
  }
  /* the card: X, Y top-left, W wide; returns {h, rows: [{x, y, maths, value}]} */
  function workCard(q, X, Y, W, tm) {
    const wk = working(q); if (!wk || !tm) return null;
    const L = wkLayout(q, W), H = L.H, C = PC();
    const geom = { h: H, rows: [] };
    const s = pop(T, tm.cardIn, 0.3, 1.4);
    if (s <= 0) return geom;
    g.save(); g.translate(X + W / 2, Y + H / 2); g.rotate(-0.008); g.scale(s, s);
    paperRect(0, 0, W, H, '#fbf5e3', { seed: 560, depth: 1.4, boil: false });
    paperRect(0, -H / 2 + 20, W - 8, 30, C.leaf, { seed: 562, depth: 0, boil: false });
    g.strokeStyle = 'rgba(90,120,70,0.25)'; g.lineWidth = 2; g.beginPath();
    for (let yy = -H / 2 + 96; yy < H / 2 - 8; yy += 52) { g.moveTo(-W / 2 + 14, yy); g.lineTo(W / 2 - 14, yy); }
    g.stroke(); g.strokeStyle = 'rgba(179,38,30,0.35)'; g.beginPath(); g.moveTo(-W / 2 + 62, -H / 2 + 40); g.lineTo(-W / 2 + 62, H / 2 - 10); g.stroke();
    tape(-W / 2 + 30, -H / 2 + 8, 90, -0.6, undefined, 561);
    g.restore();
    if (T < tm.cardIn + 0.2) return geom;
    txt('THE WORKING', X + W / 2, Y + 21, 22, { font: SLAB, color: C.cream });
    let y = Y + WL.top;
    wk.steps.forEach((st, i) => {
      const t0 = tm.steps[i], r = L.rows[i];
      r.lines.forEach((ln, j) => hand(ln, X + 78, y + WL.lrow / 2 + j * WL.lrow, WL.label, t0, { align: 'left', color: PENCIL, font: TYPE, seed: 570 + i * 5 + j, dur: 0.3 }));
      const my = y + r.lines.length * WL.lrow + WL.mrow / 2;
      hand(`${st.n}.`, X + 30, my, 44, t0, { align: 'left', color: PENCIL, seed: 575 + i, dur: 0.15 });
      if (i === wk.steps.length - 1 && T > tm.hi) {
        const f = `${WL.maths}px ${HANDF}`, x0 = X + 78 + tw(st.maths.slice(0, st.maths.lastIndexOf(st.value)), f), vw = tw(st.value, f);
        g.save(); g.globalAlpha *= 0.5 * P(T, tm.hi, tm.hi + 0.3); g.fillStyle = '#f2c94c'; g.fillRect(x0 - 8, my - 28, (vw + 16) * E.outCubic(P(T, tm.hi, tm.hi + 0.3)), 54); g.restore();
      }
      hand(st.maths, X + 78, my, WL.maths, t0 + 0.2, { align: 'left', color: NAVY, seed: 580 + i, dur: Math.min(0.9, 0.2 + st.maths.length * 0.03) });
      geom.rows[i] = { x: X + 78, y: my, maths: st.maths, value: st.value, t0 };
      y += r.h;
    });
    y += 12;
    L.ans.forEach((ln, j) => hand(ln, X + 78, y + 25 + j * 50, WL.ans, tm.answer + j * 0.2, { align: 'left', color: RED, seed: 590 + j, dur: 0.4 }));
    // QA: how many steps are written by now (every branch must show them all)
    const shown = tm.steps.filter(t0 => T >= t0 + 0.2).length;
    Vid.lastSteps = { q: q.id, n: wk.steps.length, shown, answer: T >= tm.answer, values: wk.steps.map(x => x.value) };
    if (docked() && shown > 0) Dock.text('steps-' + q.id, ['The working:'].concat(wk.steps.filter((x, i) => T >= tm.steps[i] + 0.2).map(x => `${x.n}. ${x.text}`), T >= tm.answer ? [wk.answer] : []), { cls: 'note' });
    return geom;
  }
  const stepSfx = tm => (tm ? [{ name: fx('page', 'paper'), t: tm.cardIn, gain: 0.3 }, ...tm.steps.map(t0 => ({ name: 'scribble', t: t0 + 0.2, gain: 0.18 })), { name: 'ding', t: tm.answer, gain: 0.25 }] : []);

  /* ======================================================================
     The Punnett seed tray (core-local/plate.js), filled box by box from Cross.square
     tm: {tray, top, side, box: [4 t0], hi}; o: {seed, ring: [box indices] + ringT}
     ====================================================================== */
  function punnett(sq, x, y, cell, tm, o = {}) {
    const Lz = Plate.trayLayout(x, y, cell);
    const s = pop(T, tm.tray, 0.4, 1.6); if (s <= 0) return Lz;
    const rec = sq.boxes.findIndex(b => !b.dominant);
    g.save(); g.translate(x, y); g.scale(s, s); g.translate(-x, -y);
    const topIn = T >= tm.top + 0.5, sideIn = T >= tm.side + 0.5;
    Plate.tray(x, y, cell, { top: topIn ? sq.top : null, side: sideIn ? sq.side : null, boxes: [null, null, null, null], trait: sq.key, hi: T >= tm.hi ? rec : -1, seed: o.seed || 31 });
    for (let k = 0; k < 2; k++) {
      if (!topIn) Plate.tile(sq.top[k], Lz.top[k].x, Lz.top[k].y, Lz.tileSize, { s: pop(T, tm.top + k * 0.18, 0.3, 2), seed: (o.seed || 31) + 40 + k, rot: k ? 0.03 : -0.02 });
      if (!sideIn) Plate.tile(sq.side[k], Lz.side[k].x, Lz.side[k].y, Lz.tileSize, { s: pop(T, tm.side + k * 0.18, 0.3, 2), seed: (o.seed || 31) + 44 + k, rot: k ? -0.025 : 0.02 });
    }
    sq.boxes.forEach((b, i) => {
      const sc = pop(T, tm.box[i], 0.3, 2.2); if (sc <= 0) return;
      const B = Lz.boxes[i], ts = cell * 0.27;
      g.save(); g.translate(B.x, B.y); g.scale(sc, sc);
      Plate.tile(b.genotype[0], -ts * 0.56, -cell * 0.17, ts, { seed: (o.seed || 31) + 70 + i * 2, rot: -0.03 });
      Plate.tile(b.genotype[1], ts * 0.56, -cell * 0.17, ts, { seed: (o.seed || 31) + 71 + i * 2, rot: 0.03 });
      Plate.pheno(sq.key, b.phenotype, 0, cell * 0.2, cell * 0.12, { seed: (o.seed || 31) + 80 + i });
      g.restore();
    });
    if (T >= tm.hi) inkCircle(Lz.boxes[rec].x, Lz.boxes[rec].y, cell * 0.47, { w: 6, color: '#d9a400', seed: 97, prog: P(T, tm.hi, tm.hi + 0.4), passes: 1 });
    if (o.ring && T >= o.ringT) o.ring.forEach((i, k) => inkCircle(Lz.boxes[i].x, Lz.boxes[i].y, cell * 0.45, { w: 5, color: PC().leafDark, seed: 98 + k, prog: P(T, o.ringT + k * 0.15, o.ringT + k * 0.15 + 0.4), passes: 1 }));
    g.restore();
    Vid.lastTray = { boxes: sq.boxes.filter((b, i) => T >= tm.box[i]).map(b => b.genotype), hi: T >= tm.hi ? sq.boxes[rec].genotype : null };
    return Lz;
  }
  /* a small, finished tray baked once (the "same square for every seed" of s4) */
  function miniTray(sq, x, y, cell, s) {
    if (s <= 0.001) return;
    const Lz = Plate.trayLayout(0, 0, cell), m = Lz.tileSize + cell * 0.25 + 24, rec = sq.boxes.findIndex(b => !b.dominant);
    g.save(); g.translate(x, y); g.scale(s, s);
    layer('mini|' + cell, -cell - m, -cell - m, cell * 2 + m + cell * 0.2 + 20, cell * 2 + m + cell * 0.2 + 20, () => {
      Plate.tray(0, 0, cell, { top: sq.top, side: sq.side, boxes: sq.boxes.map(b => b.genotype), trait: sq.key, hi: rec, seed: 131 });
    }, { zoom: 1.5 });
    g.restore();
  }

  /* ======================================================================
     the five scenes (BODY[id](t, part, ctx, c) draws the stage; S[id] wraps it with the chapter card)
     ====================================================================== */
  const BODY = {};
  const Vid = window.BlueEyesVideo = { BODY, nums, working, STEPCUE, layers: LY, lastSteps: null, lastTray: null };

  /* ---- s1 "Blue from where?": the family card, the chromosome pair, the SIMPLIFIED B/b model, b + b slide to Lily ---- */
  const FAM = { x: 700, y: 505, w: 560, h: 440, col: 180 };
  const famX = k => FAM.x + (k - 1) * FAM.col;
  const FAMY = { name: FAM.y - FAM.h / 2 + 62, eye: FAM.y - FAM.h / 2 + 150, word: FAM.y - FAM.h / 2 + 214, tile: FAM.y - FAM.h / 2 + 300, gran: FAM.y + FAM.h / 2 - 42 };
  const TILE1 = 60, tileDX = 36;
  const CHR = { x: [1112, 1308], y: 440, spot: -70 };
  const CHROMO = (() => {
    const L = 140, W = 30, pts = [], pinch = y => 1 - 0.34 * Math.exp(-(((y + 16) / 24) ** 2)), twist = y => Math.sin((y + L) / (2 * L) * TAU) * 5;
    for (let i = 0; i <= 18; i++) { const y = -L + i * (2 * L / 18); pts.push([W * pinch(y) + twist(y), y]); }
    for (let i = 1; i < 8; i++) { const a = i / 8 * Math.PI; pts.push([Math.cos(a) * W + twist(L), L + Math.sin(a) * W * 0.8]); }
    for (let i = 18; i >= 0; i--) { const y = -L + i * (2 * L / 18); pts.push([-W * pinch(y) + twist(y), y]); }
    for (let i = 1; i < 8; i++) { const a = Math.PI + i / 8 * Math.PI; pts.push([Math.cos(a) * W + twist(-L), -L + Math.sin(a) * W * 0.8]); }
    return Object.assign(pts, { k: 'blue:chromo' });
  })();
  function chromosome(k, s, spotT, tileLetter, tileT) {
    if (s <= 0.001) return;
    const x = CHR.x[k], y = CHR.y, col = k ? '#b9c79a' : '#d9a67a';
    g.save(); g.translate(x, y); g.rotate(k ? 0.05 : -0.05); g.scale(s, s);
    paperPiece(CHROMO, 0, 0, col, { seed: 210 + k, depth: 1.2 });
    [-118, -100, -40, 30, 62, 96, 124].forEach((by, j) => ink([[-24, by], [24, by + 2]], { w: 3, color: 'rgba(43,36,32,0.45)', seed: 220 + j + k * 9, passes: 1, step: 20 }));
    if (T >= spotT) {
      const a = P(T, spotT, spotT + 0.3);
      alpha(a, () => paperRect(0, CHR.spot, 64, 22, PC().yellowPea, { seed: 230 + k, depth: 0, boil: false }));
      inkCircle(0, CHR.spot, 34, { sx: 1.25, sy: 0.75, w: 4, color: RED, seed: 232 + k, prog: P(T, spotT, spotT + 0.4), passes: 1 });
    }
    if (tileLetter && T >= tileT) Plate.tile(tileLetter, 74, CHR.spot, 50, { s: pop(T, tileT, 0.3, 2), seed: 236 + k, rot: 0.04 });
    g.restore();
  }
  BODY.s1 = function (t, part, ctx, c) {
    const N = nums(), C = PC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q);
    bgGreen();
    pot(1470, 960, 240, { seed: 61, side: -1, s: pop(t, c.L('s1_01') - 0.6, 0.4) });     // Mr. Mendoza's potted pea plant
    const tm = {
      card: c.W('s1_01', 'My'), col: [c.W('s1_01', 'mom'), c.W('s1_01', 'dad'), c.W('s1_01', 'Mine')],
      gran: c.W('s1_02', 'brown'), pair: c.W('s1_05', 'pairs'), from: [c.W('s1_05', 'mom'), c.W('s1_05', 'dad')],
      spot: c.W('s1_06', 'gene'), alle: c.W('s1_06', 'alleles'),
      banner: c.W('s1_07', 'simplified'), keyB: c.W('s1_07', 'brown'), keyb: c.W('s1_07', 'blue'), tiles: [c.W('s1_07', 'Mom'), c.W('s1_07', 'Dad')],
      hide: branch(c, { q1_right: 'one', q1_wrong0: 'hidden', q1_wrong2: 'hide' }), slide: branch(c, { q1_right: 'both', q1_wrong0: 'pass', q1_wrong2: 'both' }),
      after: [c.W('s1_08', 'Mom'), c.W('s1_08', 'Dad')], real: c.W('s1_09', 'happens'),
    };
    // the family card (s1_01): MOM brown, DAD brown, LILY blue (eye icons); Grandma's brown too (s1_02)
    if (card(FAM.x, FAM.y, FAM.w, FAM.h, pop(t, tm.card, 0.4, 1.6), { seed: 240 })) {
      N.family.forEach((f, k) => {
        const x = famX(k); if (t < tm.col[k]) return;
        Plate.woodtype(f.who, x, FAMY.name, 34, { t0: tm.col[k], seed: 250 + k * 5, color: k === 2 ? '#dfe9f2' : C.label });
        Plate.pheno('eye', f.eye, x, FAMY.eye, 34, { seed: 260 + k, s: pop(t, tm.col[k] + 0.2, 0.35) });
        hand(`${f.eye} eyes`, x, FAMY.word, 36, tm.col[k] + 0.3, { color: k === 2 ? '#2f5f8a' : '#6b4424', seed: 265 + k, dur: 0.35 });
      });
      hand('(Grandma: brown too)', FAM.x, FAMY.gran, 30, tm.gran, { color: PENCIL, seed: 269, dur: 0.4 });
      // s1_07: Mom = B b, Dad = B b (the model's tiles); Lily's two empty slots
      for (let k = 0; k < 2; k++) {
        const x = famX(k), s = pop(t, tm.tiles[k], 0.3, 2);
        Plate.tile(N.family[k].g[0], x - tileDX, FAMY.tile, TILE1, { s, seed: 270 + k * 4, rot: -0.03 });
        Plate.tile(N.family[k].g[1], x + tileDX, FAMY.tile, TILE1, { s: pop(t, tm.tiles[k] + 0.15, 0.3, 2), seed: 271 + k * 4, rot: 0.03 });
      }
      if (t >= tm.tiles[1] + 0.2) for (let j = 0; j < 2; j++) if (t < tm.slide + j * 0.35 + 0.8) Plate.tile('x', famX(2) + (j ? tileDX : -tileDX), FAMY.tile, TILE1, { state: 'ghost', seed: 280 + j });
      // the reply (every branch): the hidden b of each parent circled, then a copy slides down to Lily: b b
      for (let k = 0; k < 2; k++) {
        const bx = famX(k) + tileDX, ht = tm.hide + k * 0.2;
        if (t >= ht) inkCircle(bx, FAMY.tile, 42, { w: 5, color: '#d9a400', seed: 284 + k, prog: P(t, ht, ht + 0.4), passes: 1 });
        const st = tm.slide + k * 0.35, u = E.inOutCubic(P(t, st, st + 0.8));
        if (t >= st) {
          const tx = famX(2) + (k ? tileDX : -tileDX), x = lerp(bx, tx, u), y = FAMY.tile - Math.sin(u * Math.PI) * (80 + k * 30);
          Plate.tile(N.kid.genotype[k], x, y, TILE1, { seed: 290 + k, rot: (1 - u) * 0.3 * (k ? 1 : -1) });
        }
      }
      const land = tm.slide + 0.35 + 0.8;
      if (t >= land) tick(famX(2) + 80, FAMY.tile + 30, 0.8, P(t, land, land + 0.3), 295);
      if (t >= tm.real) tag('happens in real families', FAM.x + 60, FAM.y - FAM.h / 2 - 34, 30, pop(t, tm.real, 0.35), { rot: 0.03, seed: 296, color: '#dfe9f2' });
    }
    if (docked()) {
      const fam = N.family.filter((f, k) => t >= tm.col[k]).map((f, k) => `${f.who}: ${f.eye} eyes` + (t >= (k < 2 ? tm.tiles[k] : tm.slide + 1) ? ` (${f.g})` : ''));
      if (fam.length) Dock.text('s1-family', fam, { cls: 'note' });
    }
    // the chromosome pair (s1_05): FROM MOM / FROM DAD, the gene spot (s1_06); in s1_08 each spot gets its b
    for (let k = 0; k < 2; k++) {
      chromosome(k, pop(t, tm.pair + k * 0.15, 0.4, 1.8), tm.spot + k * 0.15, N.kid.genotype[k], tm.after[k]);
      if (t >= tm.from[k]) tag(k ? 'FROM DAD' : 'FROM MOM', CHR.x[k], CHR.y + 196, 24, pop(t, tm.from[k], 0.35), { font: SLAB, color: k ? '#b9c79a' : '#d9a67a', seed: 300 + k, rot: k ? 0.04 : -0.04 });
    }
    hand('2 versions of a gene = alleles', (CHR.x[0] + CHR.x[1]) / 2, CHR.y - 196, 34, tm.alle, { color: NAVY, seed: 305, dur: 0.6 });
    // the model key + the SIMPLIFIED MODEL banner (s1_07): capital = the dominant word, small = the recessive
    const ky = 700;
    Plate.tile(N.eye.letter, 1100, ky, 52, { s: pop(t, tm.keyB, 0.3, 2), seed: 310 });
    hand(`= ${N.eye.dominant} (dominant)`, 1136, ky, 32, tm.keyB + 0.1, { align: 'left', seed: 311, dur: 0.4 });
    Plate.tile(N.eye.letter.toLowerCase(), 1100, ky + 62, 52, { s: pop(t, tm.keyb, 0.3, 2), seed: 312 });
    hand(`= ${N.eye.recessive}`, 1136, ky + 62, 32, tm.keyb + 0.1, { align: 'left', seed: 313, dur: 0.3 });
    const bs = pop(t, tm.banner, 0.4, 2);
    if (bs > 0) {
      g.save(); g.translate(745, 800); g.rotate(-0.015); g.scale(bs, bs);
      paperRect(0, 0, 500, 74, RED, { seed: 320, depth: 1.3, boil: false });
      paperPiece(RIB_L, -270, 6, '#8e2219', { seed: 321, depth: 0.8 }); paperPiece(RIB_R, 270, 6, '#8e2219', { seed: 322, depth: 0.8 });
      txt('SIMPLIFIED MODEL', 0, 3, 42, { font: SLAB, color: C.cream });
      g.restore();
      // on a cream scrap: over the greenhouse engraving plain pencil was hard to read (the honesty label, facts 2.6)
      if (t >= tm.banner + 0.3) paperRect(745, 874, 400, 50, PC().cream, { seed: 324, depth: 0.8, boil: false, rot: -0.01, s: pop(t, tm.banner + 0.3, 0.3, 2) });
      hand('real eye color = many genes', 745, 872, 32, tm.banner + 0.35, { color: INK, seed: 323, dur: 0.5 });
    }
    if (docked() && t >= tm.banner) Dock.text('s1-model', [`SIMPLIFIED MODEL: ${N.eye.letter} = ${N.eye.dominant}, ${N.eye.letter.toLowerCase()} = ${N.eye.recessive}`, 'Real eye color uses many genes.'].concat(t >= tm.slide + 1 ? [`Lily: ${N.kid.genotype[0]} from Mom + ${N.kid.genotype[1]} from Dad = ${N.kid.genotype} (${N.kid.phenotype}, in the model)`] : []), { cls: 'note' });
    // a wrong answer's idea on a pink sticky, struck (reply only)
    if (part.kind === 'reply' && ans === 0) sticky(['BROWN PARENTS', 'ONLY PASS BROWN'], 1215, 470, rs + 0.1, { w: 300, size: 34, color: '#f4c6c0', seed: 330, strike: c.W('q1_wrong0', 'hidden'), rot: 0.05 });
    if (part.kind === 'reply' && ans === 2) sticky(['NEEDS A BLUE-EYED', 'GRANDPARENT'], 1215, 470, rs + 0.1, { w: 320, size: 34, color: '#f4c6c0', seed: 331, strike: c.W('q1_wrong2', 'needed'), rot: 0.05 });
    cast(t, ctx);
  };
  const RIB_L = Object.assign([[40, -32], [-60, -32], [-34, 0], [-60, 32], [40, 32]], { k: 'blue:ribL' });
  const RIB_R = Object.assign([[-40, -32], [60, -32], [34, 0], [60, 32], [-40, 32]], { k: 'blue:ribR' });
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.2, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: fx('page', 'paper'), t: c.W('s1_01', 'My'), gain: 0.35 });
      ['mom', 'dad', 'Mine'].forEach(w => out.push({ name: 'pop', t: c.W('s1_01', w), gain: 0.22 }));
      out.push({ name: 'scribble', t: c.W('s1_02', 'brown'), gain: 0.18 }, { name: 'whoosh', t: c.W('s1_05', 'pairs'), gain: 0.22 });
      out.push({ name: 'paper', t: c.W('s1_05', 'mom'), gain: 0.22 }, { name: 'paper', t: c.W('s1_05', 'dad'), gain: 0.22 });
      out.push({ name: 'scribble', t: c.W('s1_06', 'gene'), gain: 0.2 }, { name: 'scribble', t: c.W('s1_06', 'alleles'), gain: 0.2 });
      out.push({ name: 'thunk', t: c.W('s1_07', 'simplified'), gain: 0.4 }, { name: 'pop', t: c.W('s1_07', 'brown'), gain: 0.2 }, { name: 'pop', t: c.W('s1_07', 'blue'), gain: 0.2 });
      out.push({ name: 'pop', t: c.W('s1_07', 'Mom'), gain: 0.25 }, { name: 'pop', t: c.W('s1_07', 'Dad'), gain: 0.25 });
      const rs = replyStart(c, q), slide = branch(c, { q1_right: 'both', q1_wrong0: 'pass', q1_wrong2: 'both' });
      out.push({ name: 'pop', t: rs + 0.1, gain: part.kind === 'reply' && ansOf(ctx, part) !== q.correct ? 0.3 : 0 });
      out.push({ name: 'scribble', t: c.W('q1_wrong0', 'hidden'), gain: 0.3 }, { name: 'scribble', t: c.W('q1_wrong2', 'needed'), gain: 0.3 });
      out.push({ name: 'whoosh', t: slide, gain: 0.3 }, { name: 'whoosh', t: slide + 0.35, gain: 0.3 }, { name: 'ding', t: slide + 1.15, gain: 0.3 });
      out.push({ name: 'pop', t: c.W('s1_08', 'Mom'), gain: 0.25 }, { name: 'pop', t: c.W('s1_08', 'Dad'), gain: 0.25 }, { name: 'paper', t: c.W('s1_09', 'happens'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s2 "Peas, please": Mendel's peas card, the seed tray filling box by box, the working, yy highlighted ---- */
  const S2 = { card: { x: 672, y: 335, w: 480, h: 220 }, tray: { x: 720, y: 700, cell: 150 }, wk: { x: 975, y: 240, w: 480 } };
  BODY.s2 = function (t, part, ctx, c) {
    const N = nums(), C = PC(), q = part.seg.question, ans = ansOf(ctx, part), rs = replyStart(c, q), sq = N.sq2, pe = N.pe;
    bgGreen();
    const tGrid = c.W('s2_03', 'grid');
    const tm = {
      pots: c.L('s2_01') - 0.3, card: c.W('s2_01', 'Mendel'), rowY: c.W('s2_02', 'Big'), rowy: c.W('s2_02', 'Little'), both: c.W('s2_02', 'Both'),
      tray: tGrid, top: c.W('s2_03', 'top'), side: c.W('s2_03', 'side'),
      box: [c.W('s2_04', 'big', 1), c.W('s2_04', 'big', 3), c.W('s2_04', 'little', 2), c.W('s2_04', 'little', 3)],
    };
    const W = stepTimes(c, q);
    tm.hi = W ? W.steps[1] + 0.2 : 1e6;
    // two potted Yy plants (s2_01–02); they sink away as the tray comes up (s2_03)
    const out = E.inCubic(P(t, tGrid - 0.4, tGrid + 0.2));
    if (out < 1) for (let k = 0; k < 2; k++) {
      const x = 760 + k * 300, y = 930 + out * 420;
      pot(x, y, 330, { seed: 70 + k * 7, side: k ? -1 : 1, s: pop(t, tm.pots + k * 0.2, 0.4) });
      if (t >= tm.both) tag(sq[k ? 'p2' : 'p1'], x + 64, y - 120, 40, pop(t, tm.both + k * 0.2, 0.35), { font: SLAB, seed: 340 + k, rot: k ? 0.06 : -0.06 });
    }
    // Mendel's peas card (s2_01–02): a REAL one-gene trait; the letters and words from TRAITS.seedColour
    const K = S2.card;
    if (card(K.x, K.y, K.w, K.h, pop(t, tm.card, 0.4, 1.6), { seed: 350, rot: -0.012 })) {
      txt("MENDEL'S PEAS", K.x, K.y - 72, 34, { font: SLAB, color: C.leafDark });
      hand('a REAL one-gene trait', K.x, K.y - 30, 32, tm.card + 0.3, { color: RED, seed: 351, dur: 0.4 });
      const rows = [[pe.letter, `${pe.dominant} ${pe.noun} (dominant)`, pe.dominant, tm.rowY], [pe.letter.toLowerCase(), `${pe.recessive} ${pe.noun}`, pe.recessive, tm.rowy]];
      rows.forEach(([L, words, v, t0], j) => {
        const ry = K.y + 22 + j * 58, x0 = K.x - K.w / 2 + 50;
        Plate.tile(L, x0, ry, 46, { s: pop(t, t0, 0.3, 2), seed: 352 + j });
        hand('= ' + words, x0 + 36, ry, 32, t0 + 0.1, { align: 'left', seed: 354 + j, dur: 0.4 });
        pea(v, K.x + K.w / 2 - 40, ry, 18, 0, pop(t, t0 + 0.3, 0.3), 356 + j);
      });
    }
    if (docked() && t >= tm.card) Dock.text('s2-card', ["MENDEL'S PEAS: a REAL one-gene trait", `${pe.letter} = ${pe.dominant} ${pe.noun} (dominant), ${pe.letter.toLowerCase()} = ${pe.recessive} ${pe.noun}`], { cls: 'note' });
    // the Punnett seed tray (s2_03–04): parent 1 on top, parent 2 down the side, the boxes fill one by one
    const T2 = S2.tray;
    const after = part.kind === 'after';
    const Lz = punnett(sq, T2.x, T2.y, T2.cell, tm, { seed: 31, ring: N.hetIdx, ringT: c.W('s2_05', 'hiding') });
    if (t >= tm.tray + 0.3) tag(`${sq.p1} × ${sq.p2}`, Lz.side[0].x - 10, Lz.top[0].y - 8, 28, pop(t, tm.tray + 0.3, 0.35), { font: SLAB, seed: 360, rot: -0.1, color: C.label });
    if (docked() && t >= tm.box[0]) Dock.text('s2-square', [`Punnett square ${sq.p1} × ${sq.p2}: ` + sq.boxes.filter((b, i) => t >= tm.box[i]).map(b => `${b.genotype} (${b.phenotype})`).join(', ')], { cls: 'note' });
    // the reply (every branch): the working; a wrong answer's number on a pink sticky, struck
    const G = workCard(q, S2.wk.x, S2.wk.y, S2.wk.w, W);
    const gy = S2.wk.y + (G ? G.h : 440) + 60;
    if (part.kind === 'reply' && ans !== q.correct && ans != null) {
      sticky([`${q.options[ans]} ${pe.recessive}?`], S2.wk.x + 170, gy + 20, rs + 0.1, { w: 260, size: 38, color: '#f4c6c0', seed: 370, strike: branch(c, { q2_wrong0: 'Green', q2_wrong1: 'hides' }), rot: 0.04 });
    }
    // after: the phenotype percents (Cross.phenotypes), then PHENOTYPE / GENOTYPE pointed at one box
    if (after || part.kind === 'lines' || part.kind === 'reply') {
      const tG = c.W('s2_05', 'Twenty-five'), tY = c.W('s2_05', 'seventy-five');
      if (t >= tG) tag(`${pe.recessive}: ${N.ph[pe.recessive]}%`, S2.wk.x + 110, gy, 36, pop(t, tG, 0.35), { color: '#dcebc6', seed: 372, rot: -0.03 });
      if (t >= tY) tag(`${pe.dominant}: ${N.ph[pe.dominant]}%`, S2.wk.x + 340, gy, 36, pop(t, tY, 0.35), { color: '#f7e3a0', seed: 373, rot: 0.03 });
      const tP = c.W('s2_06', 'phenotype'), tGe = c.W('s2_06', 'genotype'), B = Lz.boxes[1];
      if (t >= tGe) {
        hand('GENOTYPE = the letters', S2.wk.x + 40, gy + 80, 34, tGe, { align: 'left', color: NAVY, seed: 374, dur: 0.5 });
        inkArrow(S2.wk.x + 30, gy + 80, B.x + 56, B.y - T2.cell * 0.17, { w: 4, color: NAVY, seed: 375, prog: P(t, tGe + 0.3, tGe + 0.7), bulge: -20, head: 16 });
      }
      if (t >= tP) {
        hand('PHENOTYPE = what you see', S2.wk.x + 40, gy + 136, 34, tP, { align: 'left', color: C.leafDark, seed: 376, dur: 0.5 });
        inkArrow(S2.wk.x + 30, gy + 136, B.x + 30, B.y + T2.cell * 0.22, { w: 4, color: C.leafDark, seed: 377, prog: P(t, tP + 0.3, tP + 0.7), bulge: 20, head: 16 });
      }
      if (docked() && t >= tG) Dock.text('s2-after', [`${pe.dominant}: ${N.ph[pe.dominant]}%, ${pe.recessive}: ${N.ph[pe.recessive]}%`].concat(t >= tP ? ['PHENOTYPE = what you see'] : [], t >= tGe ? ['GENOTYPE = the letters'] : []), { cls: 'note' });
    }
    cast(t, ctx);
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.4, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'thunk', t: c.L('s2_01') - 0.3, gain: 0.3 }, { name: 'thunk', t: c.L('s2_01') - 0.1, gain: 0.3 }, { name: fx('page', 'paper'), t: c.W('s2_01', 'Mendel'), gain: 0.35 });
      out.push({ name: 'pop', t: c.W('s2_02', 'Big'), gain: 0.2 }, { name: 'pop', t: c.W('s2_02', 'Little'), gain: 0.2 }, { name: 'paper', t: c.W('s2_02', 'Both'), gain: 0.25 });
      out.push({ name: 'whoosh', t: c.W('s2_03', 'grid'), gain: 0.3 }, { name: 'pop', t: c.W('s2_03', 'top'), gain: 0.22 }, { name: 'pop', t: c.W('s2_03', 'side'), gain: 0.22 });
      [['big', 1], ['big', 3], ['little', 2], ['little', 3]].forEach(([w, n]) => out.push({ name: fx('pod_pop', 'pop'), t: c.W('s2_04', w, n), gain: 0.3 }));
      out.push(...stepSfx(stepTimes(c, q)));
      out.push({ name: 'scribble', t: branch(c, { q2_wrong0: 'Green', q2_wrong1: 'hides' }), gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s2_05', 'Twenty-five'), gain: 0.2 }, { name: 'paper', t: c.W('s2_05', 'seventy-five'), gain: 0.2 }, { name: 'scribble', t: c.W('s2_05', 'hiding'), gain: 0.2 });
      out.push({ name: 'scribble', t: c.W('s2_06', 'phenotype'), gain: 0.2 }, { name: 'scribble', t: c.W('s2_06', 'genotype'), gain: 0.2 });
      return out;
    },
  };

  /* ---- s3 "Dominant isn't a popularity contest": the sticky struck, what dominant means, Mendel's ledger, 3 : 1 ---- */
  const S3 = { st: { x: 640, y: 350 }, def: { x: 705, y: 650, w: 520, h: 330 }, lg: { x: 1230, y: 470, w: 480, h: 450 }, bar: { x: 1000, y: 780, w: 460 } };
  const NUMW = { 7: 'SEVEN' };
  function ledger(N, t, tm) {
    const L = S3.lg, C = PC(), m = N.m, tot = N.tot;
    const u = E.outCubic(P(t, tm.slide, tm.slide + 0.6)); if (u <= 0) return;
    g.save(); g.translate(lerp(2100, L.x, u), L.y); g.rotate(0.012 * (1 - u) + 0.01);
    paperRect(0, 0, L.w, L.h, '#f3e8cc', { seed: 400, depth: 1.5, boil: false });
    g.strokeStyle = 'rgba(80,60,35,0.35)'; g.lineWidth = 2; g.strokeRect(-L.w / 2 + 14, -L.h / 2 + 14, L.w - 28, L.h - 28);
    for (let yy = -L.h / 2 + 118; yy < L.h / 2 - 20; yy += 50) ink([[-L.w / 2 + 30, yy], [L.w / 2 - 30, yy + 1]], { w: 1.5, color: 'rgba(80,60,35,0.25)', seed: 401 + yy, passes: 1, step: 80 });
    g.restore();
    const x0 = L.x - L.w / 2 + 40, top = L.y - L.h / 2;
    if (u < 1) return;
    txt(`MENDEL ${MENDEL_YEAR} · SEED COLOR`, L.x, top + 50, 26, { font: TYPE, color: C.woodDark });
    hand(`${fmtInt(m.d)} ${m.dominant}`, x0, top + 118, 48, tm.d, { align: 'left', color: '#8a6a10', seed: 402, dur: 0.5 });
    hand(`${fmtInt(m.r)} ${m.recessive}`, x0, top + 172, 48, tm.r, { align: 'left', color: C.leafDark, seed: 403, dur: 0.5 });
    const xr = L.x + L.w / 2 - 36;
    hand(`= ${m.ratio.toFixed(2)} : 1`, xr, top + 145, 48, tm.ratio, { align: 'right', color: RED, seed: 404, dur: 0.4 });
    ink([[x0, top + 222], [L.x + L.w / 2 - 40, top + 224]], { w: 2.5, color: 'rgba(43,36,32,0.5)', seed: 405, prog: P(t, tm.all - 0.2, tm.all + 0.2), passes: 1, step: 60 });
    txt(`ALL ${NUMW[Cross.MENDEL.length] || Cross.MENDEL.length} TRAITS`, L.x, top + 262, 26, { font: TYPE, color: C.woodDark, alpha: P(t, tm.all, tm.all + 0.3) });
    hand(`${fmtInt(tot.d)} : ${fmtInt(tot.r)}`, x0, top + 330, 44, tm.all + 0.2, { align: 'left', color: INK, seed: 406, dur: 0.5 });
    hand(`= ${tot.ratio.toFixed(2)} : 1`, xr, top + 385, 48, tm.all + 0.6, { align: 'right', color: RED, seed: 407, dur: 0.4 });
  }
  /* a bar of pea icons in Mendel's proportion (N.barY of N.BAR yellow), baked, revealed left to right */
  function peaBar(N, x, y, w, t0) {
    if (T < t0) return;
    const n = N.BAR, r = Math.floor(w / n / 2) - 3, pitch = w / n;
    const draw = () => { for (let i = 0; i < n; i++) Plate.pheno('pea', i < N.barY ? N.m.dominant : N.m.recessive, x + pitch * (i + 0.5), y, r, { seed: i < N.barY ? 5 : 6 }); };
    const pr = E.outCubic(P(T, t0, t0 + 0.8));
    g.save(); g.beginPath(); g.rect(x - 10, y - r - 16, (w + 20) * pr, 2 * r + 32); g.clip();
    layer('peabar|' + w, x - 10, y - r - 16, w + 20, 2 * r + 32, draw);
    g.restore();
    hand(`${N.barY} : ${n - N.barY}, about 3 : 1`, x + w / 2, y + r + 40, 34, t0 + 0.6, { color: INK, seed: 410, dur: 0.4 });
  }
  BODY.s3 = function (t, part, ctx, c) {
    const N = nums(), C = PC(), q = part.seg.question, rs = replyStart(c, q), pe = N.pe;
    bgShed();
    const tm = {
      st: c.W('s3_01', 'dominant'), l: [c.W('s3_01', 'dominant') + 0.2, c.W('s3_01', 'more'), c.W('s3_01', 'stronger')],
      pod: c.W('s3_02', 'pod'), strike: rs + 0.3, def: rs + 0.6, rare: c.W('s3_03', 'rare'),
      slide: c.W('s3_04', 'Mendel'), d: c.W('s3_04', 'Six'), r: c.W('s3_04', 'two'), ratio: c.W('s3_04', 'three'),
    };
    tm.all = Math.max(tm.ratio + 0.8, tm.slide + 1.6);
    // Lily's sticky (s3_01), struck on every branch of the reply
    sticky(['DOMINANT =', 'MORE COMMON =', 'STRONGER'], S3.st.x, S3.st.y, tm.st - 0.2, { w: 380, size: 42, seed: 380, lineT: tm.l, strike: tm.strike, rot: -0.04 });
    if (docked() && t >= tm.st) Dock.text('s3-sticky', ['Lily: DOMINANT = MORE COMMON = STRONGER' + (t >= tm.strike ? ' (crossed out)' : '')], { cls: 'note' });
    // Mr. Mendoza hands her a pod (s3_02)
    podProp(915, 330, 140, -0.35, pop(t, tm.pod, 0.4), 381);
    // what dominant means (the reply): the correct option, then the three genotypes and what shows (Cross.phenotype)
    const D = S3.def;
    if (card(D.x, D.y, D.w, D.h, pop(t, tm.def, 0.35, 1.5), { seed: 385, rot: 0.01 })) {
      txt('DOMINANT means…', D.x, D.y - D.h / 2 + 40, 30, { font: TYPE, color: C.leafDark });
      const lines = wrapLines(String(q.options[q.correct]), D.w - 60, 36);
      lines.forEach((l, j) => hand(l, D.x, D.y - D.h / 2 + 90 + j * 42, 36, tm.def + 0.2 + j * 0.3, { color: NAVY, seed: 386 + j, dur: 0.5 }));
      Cross.genotypes3('seedColour').forEach((gt, k) => {
        const x = D.x + (k - 1) * 160, y = D.y + D.h / 2 - 90, t0 = tm.def + 0.9 + k * 0.35, s = pop(t, t0, 0.3, 2);
        Plate.tile(gt[0], x - 26, y, 44, { s, seed: 390 + k * 3, rot: -0.03 });
        Plate.tile(gt[1], x + 26, y, 44, { s, seed: 391 + k * 3, rot: 0.03 });
        pea(Cross.phenotype('seedColour', gt), x, y + 56, 18, 0, pop(t, t0 + 0.2, 0.3), 392 + k * 3);
      });
      hand('one copy is enough', D.x, D.y + D.h / 2 + 30, 32, tm.def + 2.1, { color: RED, seed: 399, dur: 0.4 });
      if (t >= tm.rare) tag('not common, not better', D.x + 150, D.y - D.h / 2 - 10, 28, pop(t, tm.rare, 0.35), { rot: 0.05, seed: 398, color: '#f4c6c0' });
      if (docked()) Dock.text('s3-def', ['DOMINANT means: ' + q.options[q.correct]].concat(Cross.genotypes3('seedColour').map(gt => `${gt}: ${Cross.phenotype('seedColour', gt)}`)), { cls: 'note' });
    }
    // Mendel's ledger (s3_04) and a 3 : 1 bar of pea icons
    ledger(N, t, tm);
    peaBar(N, S3.bar.x, S3.bar.y, S3.bar.w, tm.all + 0.6);
    if (docked() && t >= tm.slide + 0.6) Dock.text('s3-ledger', [`Mendel ${MENDEL_YEAR}, seed color: ${fmtInt(N.m.d)} ${N.m.dominant} : ${fmtInt(N.m.r)} ${N.m.recessive} = ${N.m.ratio.toFixed(2)} : 1`, `All ${Cross.MENDEL.length} traits: ${fmtInt(N.tot.d)} : ${fmtInt(N.tot.r)} = ${N.tot.ratio.toFixed(2)} : 1`], { cls: 'note' });
    cast(t, ctx);
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.6, after: 3.2 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, rs = replyStart(c, q), out = chapterSfx(part);
      out.push({ name: 'paper', t: c.W('s3_01', 'dominant') - 0.2, gain: 0.3 }, { name: 'scribble', t: c.W('s3_01', 'more'), gain: 0.18 }, { name: 'scribble', t: c.W('s3_01', 'stronger'), gain: 0.18 });
      out.push({ name: fx('pod_pop', 'pop'), t: c.W('s3_02', 'pod'), gain: 0.4 });
      out.push({ name: 'scribble', t: rs + 0.3, gain: 0.35 }, { name: fx('page', 'paper'), t: rs + 0.6, gain: 0.3 });
      [0, 1, 2].forEach(k => out.push({ name: 'pop', t: rs + 1.5 + k * 0.35, gain: 0.2 }));
      out.push({ name: 'paper', t: c.W('s3_03', 'rare'), gain: 0.2 }, { name: 'whoosh', t: c.W('s3_04', 'Mendel'), gain: 0.35 });
      out.push({ name: 'scribble', t: c.W('s3_04', 'Six'), gain: 0.18 }, { name: 'scribble', t: c.W('s3_04', 'two'), gain: 0.18 }, { name: 'ding', t: c.W('s3_04', 'three'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s4 "Is green due?": three yellow peas roll out, the same square for every seed, the working, one plant 20 : 19 ---- */
  const S4 = { pod: { x: 500, y: 300 }, slots: [560, 715, 870, 1025], slotY: 410, mini: { y: 560, cell: 50 }, st: { x: 640, y: 780 }, wk: { x: 1100, y: 236, w: 380 }, lg: { x: 760, y: 800, w: 620, h: 210 } };
  /* Mendel's one plant (Cross.SINGLE_PLANTS): the counts as rows of peas, baked, revealed */
  function peaRow(n, value, x, y, t0, key) {
    if (T < t0 || n <= 0) return;
    const r = 8, pitch = 20, w = n * pitch;
    const pr = E.outCubic(P(T, t0, t0 + 0.7));
    g.save(); g.beginPath(); g.rect(x - 4, y - 16, (w + 8) * pr, 32); g.clip();
    layer('row|' + key, x - 4, y - 16, w + 8, 32, () => { for (let i = 0; i < n; i++) Plate.pheno('pea', value, x + pitch * (i + 0.5), y, r, { seed: 7 }); });
    g.restore();
  }
  BODY.s4 = function (t, part, ctx, c) {
    const N = nums(), C = PC(), q = part.seg.question, rs = replyStart(c, q), sq = N.sq2, pe = N.pe, after = part.kind === 'after';
    bgGreen();
    const W = stepTimes(c, q);
    const tm = {
      pod: c.L('s4_01') - 0.3, peas: [c.W('s4_01', 'Yellow', 1), c.W('s4_01', 'Yellow', 2), c.W('s4_01', 'Yellow', 3)], next: c.W('s4_01', 'next'), due: c.W('s4_01', 'due'),
      know: c.W('s4_02', 'know'), mini: W ? W.steps[0] : 1e6, every: c.W('s4_03', 'Every'),
      chances: c.W('s4_04', 'chances'), plant: c.W('s4_04', 'Mendel'), y20: c.W('s4_04', 'twenty'), g19: c.W('s4_04', 'nineteen'),
      circle: c.W('s4_05', 'Nineteen'), thou: c.W('s4_06', 'Thousands'),
    };
    // the pod (s4_01) and the three yellow seeds rolling out one by one (the story's peas, not a simulation)
    podProp(S4.pod.x, S4.pod.y, 150, -0.25, pop(t, tm.pod, 0.4), 420);
    for (let k = 0; k < 3; k++) {
      const t0 = tm.peas[k]; if (t < t0 - 0.35) continue;
      const u = E.outCubic(P(t, t0 - 0.35, t0 + 0.25)), x0 = S4.pod.x + 60, y0 = S4.pod.y + 20, x1 = S4.slots[k];
      const x = lerp(x0, x1, u), y = lerp(y0, S4.slotY, u) - Math.sin(u * Math.PI) * 40;
      pea(pe.dominant, x, y, 26, (x - x0) / 26, 1, 421 + k);
    }
    // the next seed's slot (s4_01: "the next one"), and s4_02's question mark
    if (t >= tm.next) {
      const x = S4.slots[3], a = P(t, tm.next, tm.next + 0.3);
      alpha(a, () => { g.save(); g.setLineDash([8, 8]); g.lineWidth = 4; g.strokeStyle = PENCIL; g.beginPath(); g.arc(x, S4.slotY, 28, 0, TAU); g.stroke(); g.restore(); });
      hand('?', x, S4.slotY + 2, 48, tm.know, { color: RED, seed: 425, dur: 0.2 });
      hand('next', x, S4.slotY - 52, 28, tm.next, { color: PENCIL, seed: 426, dur: 0.3 });
    }
    // Lily's sticky (s4_01), struck when the working starts; it gives way to Mendel's plant (s4_04)
    const stA = 1 - P(t, tm.plant - 0.4, tm.plant);
    sticky(['THREE YELLOW.', 'GREEN IS DUE!'], S4.st.x, S4.st.y, tm.due - 0.1, { w: 360, size: 42, seed: 430, strike: W ? W.steps[0] : null, rot: -0.04, alpha: stA });
    if (docked() && t >= tm.due && stA > 0) Dock.text('s4-sticky', ['Lily: THREE YELLOW. GREEN IS DUE!' + (W && t >= W.steps[0] ? ' (crossed out)' : '')], { cls: 'note' });
    // the reply (every branch): the same square under every seed (Cross.square, baked once), the working
    for (let k = 0; k < 4; k++) {
      const t0 = tm.mini + k * 0.3;
      miniTray(sq, S4.slots[k] + 8, S4.mini.y, S4.mini.cell, pop(t, t0, 0.35, 1.8));
      // the same chance under every seed: written as the working lands (reply), there from the start of the after part
      if (W) hand(`${N.pGreen}% ${pe.recessive}`, S4.slots[k] + 8, S4.mini.y + S4.mini.cell + 38, 28, W.hi + k * 0.2, { color: k === 3 ? RED : C.leafDark, seed: 432 + k, dur: 0.3 });
    }
    workCard(q, S4.wk.x, S4.wk.y, S4.wk.w, W);
    // after: chances, not head counts; Mendel's one plant (s4_04), 19 green circled (s4_05); thousands settle at 3 : 1
    const G = S4.lg, pl = N.plant;
    if (card(G.x, G.y, G.w, G.h, pop(t, tm.plant, 0.35, 1.5), { seed: 440, rot: -0.01 })) {
      txt(`MENDEL · ONE PLANT · SEED COLOR`, G.x, G.y - G.h / 2 + 34, 24, { font: TYPE, color: C.woodDark });
      const x0 = G.x - G.w / 2 + 30;
      hand(`${pl.d} ${pe.dominant}`, x0, G.y - 18, 40, tm.y20, { align: 'left', color: '#8a6a10', seed: 441, dur: 0.4 });
      peaRow(pl.d, pe.dominant, x0 + 180, G.y - 18, tm.y20 + 0.2, 'd' + pl.d);
      hand(`${pl.r} ${pe.recessive}`, x0, G.y + 44, 40, tm.g19, { align: 'left', color: C.leafDark, seed: 442, dur: 0.4 });
      peaRow(pl.r, pe.recessive, x0 + 180, G.y + 44, tm.g19 + 0.2, 'r' + pl.r);
      if (t >= tm.circle) inkCircle(x0 + 70, G.y + 44, 80, { sx: 1, sy: 0.45, w: 6, color: RED, seed: 443, prog: P(t, tm.circle, tm.circle + 0.4) });
    }
    const ny = S4.wk.y + (W ? wkLayout(q, S4.wk.w).H : 480) + 40;
    hand('chances, not head counts', S4.wk.x + S4.wk.w / 2, ny, 36, tm.chances, { color: RED, seed: 444, dur: 0.6 });
    if (t >= tm.thou) {
      tag(`thousands: ${fmtInt(N.m.d)} : ${fmtInt(N.m.r)}`, S4.wk.x + S4.wk.w / 2, ny + 66, 30, pop(t, tm.thou, 0.35), { seed: 445, rot: 0.02 });
      hand(`about 3 : 1 (the long run)`, S4.wk.x + S4.wk.w / 2, ny + 124, 32, tm.thou + 0.4, { color: NAVY, seed: 446, dur: 0.4 });
    }
    if (docked() && t >= tm.chances) Dock.text('s4-after', ['A Punnett square gives chances, not head counts.'].concat(t >= tm.y20 ? [`Mendel, one plant: ${pl.d} ${pe.dominant}, ${pl.r} ${pe.recessive}`] : [], t >= tm.thou ? [`Thousands of seeds: ${fmtInt(N.m.d)} : ${fmtInt(N.m.r)} about 3 : 1`] : []), { cls: 'note' });
    void after; void rs;
    cast(t, ctx);
  };
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.4, after: 2.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part), W = stepTimes(c, q);
      out.push({ name: fx('pod_pop', 'pop'), t: c.L('s4_01') - 0.3, gain: 0.4 });
      [1, 2, 3].forEach(n => out.push({ name: fx('rolling', 'pop'), t: c.W('s4_01', 'Yellow', n) - 0.3, gain: 0.25 }));
      out.push({ name: 'paper', t: c.W('s4_01', 'due') - 0.1, gain: 0.3 }, { name: 'scribble', t: c.W('s4_02', 'know'), gain: 0.2 });
      if (W) { out.push({ name: 'scribble', t: W.steps[0], gain: 0.3 }); [0, 1, 2, 3].forEach(k => out.push({ name: 'pop', t: W.steps[0] + k * 0.3, gain: 0.18 })); }
      out.push(...stepSfx(W));
      out.push({ name: 'scribble', t: c.W('s4_03', 'Every'), gain: 0.2 }, { name: 'scribble', t: c.W('s4_04', 'chances'), gain: 0.2 });
      out.push({ name: fx('page', 'paper'), t: c.W('s4_04', 'Mendel'), gain: 0.35 }, { name: 'scribble', t: c.W('s4_05', 'Nineteen'), gain: 0.3 }, { name: 'paper', t: c.W('s4_06', 'Thousands'), gain: 0.25 });
      return out;
    },
  };

  /* ---- s5 "Hidden, not gone": the WHAT I LEARNED seed packet, the tongue-rolling clipping, a pea planted, INHERITED ---- */
  const S5 = { pk: { x: 790, y: 590, w: 660, h: 660 }, clip: { x: 1290, y: 400, w: 360, h: 270 }, pot: { x: 1300, y: 900 } };
  BODY.s5 = function (t, part, ctx, c) {
    const N = nums(), C = PC(), pe = N.pe;
    bgShed();
    const tm = { pk: c.L('s5_01') - 0.3, tongue: c.W('s5_03', 'Tongue'), wrong: c.W('s5_03', 'wrong'), potT: c.W('s5_05', 'Plant'), hand: c.W('s5_05', 'this'), drop: c.W('s5_06', 'calling'), p75: c.W('s5_06', 'Seventy-five'), stamp: c.W('s5_06', 'now') };
    const lt = [c.L('s5_01') + 0.6, c.W('s5_01', 'hidden'), c.W('s5_02', 'Hidden'), c.W('s5_02', 'pea')];
    for (let i = 1; i < lt.length; i++) lt[i] = Math.max(lt[i], lt[i - 1] + 1.6);
    // the seed packet: kraft body, flap, a cream label with the four lines in Lily's hand
    const K = S5.pk, s = pop(t, tm.pk, 0.45, 1.5);
    if (s > 0) {
      g.save(); g.translate(K.x, K.y); g.rotate(-0.012); g.scale(s, s);
      paperRect(0, 0, K.w, K.h, C.kraft, { seed: 460, depth: 1.5, boil: false });
      paperPiece(PKFLAP, 0, 0, C.kraftDark, { seed: 461, depth: 0.5, boil: false });
      paperRect(0, 40, K.w - 70, K.h - 170, C.label, { seed: 462, depth: 0.3, boil: false });
      paperRect(0, 40 - (K.h - 170) / 2 + 12, K.w - 70, 14, C.leaf, { seed: 463, depth: 0, boil: false });
      g.restore();
      Plate.woodtype('WHAT I LEARNED', K.x, K.y - K.h / 2 + 50, 40, { t0: tm.pk + 0.2, seed: 464, color: C.label });
      let y = K.y - K.h / 2 + 180;
      LEARNED.forEach((l, i) => {
        wrapLines(l, K.w - 130, 36).forEach((ln, j) => { hand(ln, K.x - K.w / 2 + 62 + (j ? 30 : 0), y, 36, lt[i] + j * 0.5, { align: 'left', color: '#23411f', seed: 470 + i * 4 + j, dur: Math.min(1.1, 0.2 + ln.length * 0.025) }); y += 44; });
        y += 14;
      });
    }
    if (docked()) { const shown = LEARNED.filter((l, i) => t >= lt[i]); if (shown.length) Dock.text('s5-learned', ['WHAT I LEARNED'].concat(shown), { cls: 'note' }); }
    // the clipping (s5_03): science corrected itself (facts 5.7)
    const CL = S5.clip, cs = pop(t, tm.tongue, 0.4, 1.6);
    if (cs > 0) {
      g.save(); g.translate(CL.x, CL.y); g.rotate(0.04); g.scale(cs, cs);
      texScrap('tex_news', 0, 0, CL.w, CL.h, { seed: 490, depth: 1.2, tint: '#f4eedf', tintAlpha: 0.9 });
      tape(-CL.w / 2 + 40, -CL.h / 2 + 6, 90, -0.5, undefined, 491);
      g.restore();
      txt(CLIPPING.head, CL.x - 4, CL.y - CL.h / 2 + 50, 30, { font: SLAB, color: INK, rot: 0.04 });
      wrapLines(CLIPPING.body, CL.w - 50, 28, TYPE).forEach((ln, j) => txt(ln, CL.x - 4 - j * 1.2, CL.y - CL.h / 2 + 104 + j * 36, 28, { font: TYPE, color: INK, rot: 0.04, alpha: P(t, tm.tongue + 0.3 + j * 0.15, tm.tongue + 0.5 + j * 0.15) }));
      if (t >= tm.wrong) ink([[CL.x - 150, CL.y - CL.h / 2 + 74], [CL.x + 150, CL.y - CL.h / 2 + 86]], { w: 5, color: RED, seed: 492, prog: P(t, tm.wrong, tm.wrong + 0.4), passes: 1, step: 20 });
      if (docked()) Dock.text('s5-clip', [CLIPPING.head + ' ' + CLIPPING.body], { cls: 'note' });
    }
    // a pea to plant (s5_05), dropped into the pot (s5_06); Lily's call from the model's phenotype percent
    pot(S5.pot.x, S5.pot.y, 0, { seed: 495, s: pop(t, tm.potT, 0.35) });
    if (t >= tm.hand) {
      const u = E.inCubic(P(t, tm.drop, tm.drop + 0.45)), y = lerp(S5.pot.y - 190, S5.pot.y - 92, u);
      if (u < 1) pea(pe.dominant, S5.pot.x, y, 20, u * 3, pop(t, tm.hand, 0.3), 496);
      else { const sp = E.outBack(P(t, tm.drop + 0.6, tm.drop + 1.3), 1.4); if (sp > 0) { g.save(); g.translate(S5.pot.x, S5.pot.y - 92); g.scale(sp, sp); ink([[0, 0], [2, -24], [-2, -44]], { w: 5, color: C.leafDark, seed: 497, passes: 1, step: 8 }); paperPiece(SPROUT, 0, -44, C.leaf, { seed: 498, depth: 0.3 }); paperPiece(SPROUT, 0, -40, C.leaf, { seed: 499, depth: 0.3, sx: -1 }); g.restore(); } }
    }
    if (t >= tm.p75) tag(`P(${pe.dominant}) = ${N.ph[pe.dominant]}%`, S5.pot.x, S5.pot.y - 200, 34, pop(t, tm.p75, 0.35), { font: HANDF, seed: 500, rot: -0.04, color: '#f7e3a0' });
    if (docked() && t >= tm.p75) Dock.text('s5-call', [`Lily's call: P(${pe.dominant}) = ${N.ph[pe.dominant]}%`], { cls: 'note' });
    // INHERITED lands on the packet (s5_06)
    stampMark('INHERITED', K.x + 150, K.y + K.h / 2 - 36, stampS(t, tm.stamp) * 0.9, { rot: -0.12, size: 66, color: C.leafDark });
    cast(t, ctx);
  };
  const PKFLAP = Object.assign([[-330, -330], [330, -330], [330, -262], [0, -224], [-330, -262]], { k: 'blue:pkflap' });
  const SPROUT = Object.assign([[0, 0], [22, -10], [36, 0], [22, 8]], { k: 'blue:sprout' });
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 3.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: fx('page', 'paper'), t: c.L('s5_01') - 0.3, gain: 0.35 });
      [c.L('s5_01') + 0.6, c.W('s5_01', 'hidden'), c.W('s5_02', 'Hidden'), c.W('s5_02', 'pea')].forEach(t0 => out.push({ name: 'scribble', t: t0, gain: 0.16 }));
      out.push({ name: 'paper', t: c.W('s5_03', 'Tongue'), gain: 0.35 }, { name: 'scribble', t: c.W('s5_03', 'wrong'), gain: 0.25 });
      out.push({ name: 'thunk', t: c.W('s5_05', 'Plant'), gain: 0.3 }, { name: 'pop', t: c.W('s5_05', 'this'), gain: 0.25 });
      out.push({ name: fx('watering', 'sparkle'), t: c.W('s5_06', 'calling') + 0.5, gain: 0.35 }, { name: 'paper', t: c.W('s5_06', 'Seventy-five'), gain: 0.25 });
      out.push({ name: 'stamp', t: c.W('s5_06', 'now'), gain: 0.6 }, { name: 'sparkle', t: c.W('s5_06', 'now') + 0.3, gain: 0.35 });
      return out;
    },
  };

  /* a segment without its own scene (a designer adds one later): its storyboard note on a card */
  const STAGE = {
    draw(t, part, ctx) {
      bgShed();
      const lines = wrapLines(part.seg.visual || String(part.seg.chapter || part.seg.id), 760, 34).slice(0, 7);
      card(960, 480, 860, 80 + lines.length * 44, 1, { seed: 12 });
      lines.forEach((l, i) => txt(l, 960, 480 - lines.length * 22 + 22 + i * 44, 34, { color: NAVY }));
      cast(t, ctx);
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5 && e.gain > 0);
  }

  class VideoScene extends Player.Scene {
    enter() {
      NUMS = null; WK.clear(); WLAY.clear();          // recomputed from the loaded content
      for (const sg of (Game.content.script || { segments: [] }).segments) if (!S[sg.id]) S[sg.id] = STAGE;
      super.enter();
      // bake the backdrops in spare frame time while the first chapter card is up
      if (typeof prewarm === 'function' && prewarm.draw) { prewarm.draw(() => bgGreen()); prewarm.draw(() => bgShed()); }
    }
  }
  Levels.video = VideoScene;
})();
